
import { API_URL, GOOGLE_SHEETS_URL } from '../config/api';
import { jsPDF } from 'jspdf';
import { toAPIDate } from '../utils/dateUtils';

// Get room report data
export const getRoomReportData = async (roomNo) => {
  try {
    const response = await fetch(`${API_URL}/api/manufacturing/room/${roomNo}/report`);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to get report data with status: ${response.status}`);
    }
    
    const data = await response.json();
    return data.inventory || [];
  } catch (error) {
    console.error('Failed to get room report data:', error);
    throw error;
  }
};

// Columns mirror the on-screen inventory table (RoomManufacturingWithContext.jsx)
// so the report always has the same fields the room sees, in the same order.
const REPORT_COLUMNS = [
  { label: 'Order No', key: 'orderNo', width: 0.09 },
  { label: 'WigCode', key: 'barcode', width: 0.11 },
  { label: 'Task', key: 'roomTask', width: 0.14 },
  { label: 'Start Date', key: 'scanInDate', width: 0.11, isDate: true },
  { label: 'Due Date', key: 'dueDate', width: 0.11, isDate: true },
  { label: 'Status', key: 'status', width: 0.12 },
  { label: 'Worker', key: 'worker', width: 0.13 },
  { label: 'Customer', key: 'customer', width: 0.19 },
];

const formatReportCell = (item, column) => {
  if (column.isDate) {
    return item[column.key] ? new Date(item[column.key]).toLocaleDateString() : 'N/A';
  }
  return item[column.key] || '';
};

// Generate PDF report
export const generateRoomReport = async (roomNo, reportData = null, filterLabel = null) => {
  try {
    // Get data if not provided
    const data = reportData || await getRoomReportData(roomNo);

    if (!data || data.length === 0) {
      throw new Error('No data available for report');
    }

    // Landscape gives enough width to fit all 8 columns legibly
    const doc = new jsPDF({ orientation: 'landscape' });

    // Add DiNI Wigs header
    doc.setFontSize(20);
    doc.setTextColor(212, 175, 55); // DiNI Gold
    doc.text('DiNI Wigs Manufacturing Report', 20, 25);

    // Add room and date info
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(`Room ${roomNo}`, 20, 40);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 20, 50);
    doc.text(`Total Items: ${data.length}`, 20, 60);

    let headerBottomY = 60;
    if (filterLabel) {
      headerBottomY += 10;
      doc.text(`Filters: ${filterLabel}`, 20, headerBottomY);
    }

    const tableRows = data.map(item => REPORT_COLUMNS.map(col => formatReportCell(item, col)));

    // Create table manually
    let currentY = headerBottomY + 15;
    const lineHeight = 10;
    const pageWidth = doc.internal.pageSize.width;
    const margin = 20;
    const tableWidth = pageWidth - (margin * 2);

    // Column widths, proportional to the page width
    const colWidths = REPORT_COLUMNS.map(col => col.width * tableWidth);
    const colX = [margin];
    for (let i = 1; i < colWidths.length; i++) {
      colX[i] = colX[i - 1] + colWidths[i - 1];
    }

    const drawHeaderRow = (y) => {
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.setFillColor(212, 175, 55); // DiNI Gold
      doc.setTextColor(255, 255, 255);
      doc.rect(margin, y, tableWidth, lineHeight, 'F');
      REPORT_COLUMNS.forEach((col, i) => {
        doc.text(col.label, colX[i] + 2, y + 7);
      });
    };

    drawHeaderRow(currentY);
    currentY += lineHeight;

    // Table rows
    doc.setFont(undefined, 'normal');
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);

    tableRows.forEach((row, index) => {
      // Alternate row colors
      if (index % 2 === 0) {
        doc.setFillColor(248, 249, 250);
        doc.rect(margin, currentY, tableWidth, lineHeight, 'F');
      }

      // Add row data
      row.forEach((cellValue, i) => {
        const text = String(cellValue || '');
        // Truncate text if too long
        const maxWidth = colWidths[i] - 4;
        let displayText = text;
        if (doc.getTextWidth(text) > maxWidth) {
          while (doc.getTextWidth(displayText + '...') > maxWidth && displayText.length > 0) {
            displayText = displayText.slice(0, -1);
          }
          displayText += '...';
        }
        doc.text(displayText, colX[i] + 2, currentY + 7);
      });

      currentY += lineHeight;

      // Check if we need a new page
      if (currentY > doc.internal.pageSize.height - 30) {
        doc.addPage();
        currentY = 30; // Reset Y position for new page

        drawHeaderRow(currentY);
        currentY += lineHeight;
        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        doc.setTextColor(0, 0, 0);
      }
    });

    // Add footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(
        `Page ${i} of ${pageCount} - DiNI Wigs Manufacturing System`,
        20,
        doc.internal.pageSize.height - 10
      );
    }

    return doc;

  } catch (error) {
    console.error('Failed to generate report:', error);
    throw error;
  }
};

// Download PDF report
export const downloadRoomReport = async (roomNo, sortedData = null, filterLabel = null) => {
  try {
    const doc = await generateRoomReport(roomNo, sortedData, filterLabel);
    const fileName = `DiNI_Room_${roomNo}_Report_${toAPIDate(new Date())}.pdf`;
    doc.save(fileName);
    return { success: true, fileName };
  } catch (error) {
    console.error('Failed to download report:', error);
    throw error;
  }
};

// Preview PDF report (opens in new tab)
export const previewRoomReport = async (roomNo, sortedData = null, filterLabel = null) => {
  try {
    const doc = await generateRoomReport(roomNo, sortedData, filterLabel);
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');
    return { success: true };
  } catch (error) {
    console.error('Failed to preview report:', error);
    throw error;
  }
};

export const exportToGoogleSheets = async (roomNo, sortedData = null, filterLabel = null) => {
  if (!sortedData || sortedData.length === 0) {
    throw new Error('No data to export. Please generate a report first.');
  }

  try {
    const headers = REPORT_COLUMNS.map(col => col.label);
    const rows = sortedData.map(item => REPORT_COLUMNS.map(col => formatReportCell(item, col)));

    const csvRows = [headers, ...rows].map(row =>
      row.map(cell => `"${String(cell || '').replace(/"/g, '""')}"`).join(',')
    ).join('\n');

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = GOOGLE_SHEETS_URL;
    form.target = '_blank';
    form.style.display = 'none';

    const roomInput = document.createElement('input');
    roomInput.type = 'hidden';
    roomInput.name = 'roomNo';
    roomInput.value = roomNo;
    form.appendChild(roomInput);

    if (filterLabel) {
      const filtersInput = document.createElement('input');
      filtersInput.type = 'hidden';
      filtersInput.name = 'filters';
      filtersInput.value = filterLabel;
      form.appendChild(filtersInput);
    }

    const dataInput = document.createElement('input');
    dataInput.type = 'hidden';
    dataInput.name = 'data';
    dataInput.value = csvRows;
    form.appendChild(dataInput);

    document.body.appendChild(form);
    form.submit();

    setTimeout(() => {
      document.body.removeChild(form);
    }, 1000);

    return { success: true, message: 'Google Sheet is being created...' };

  } catch (error) {
    console.error('Google Sheets export error:', error);
    throw error;
  }
};